import React, { Component } from 'react';
import { connect } from 'react-redux';
import ActionBar from '../ActionBar';
import {
  addShift,
  changeShift,
  changeShiftField,
  removeMultipleShifts,
  removeShift,
} from 'redux/ducks/Post/Shifts/actions';
import moment from 'moment';
import { saveAsDraft } from 'redux/ducks/Post/actions';
import { validateForm } from 'redux/ducks/Post';
import DaysListItem from './DaysListItem';
import { AddButton } from 'components/V2/Buttons';
import { shiftsByLocation, trackMixpanelPageView, isEndTimeAfterNow } from 'helpers/post';

class TournamentSchedule extends Component {
  constructor(props) {
    super(props);
    props.initialize('TournamentSchedule');
  }

  tournamentDaysInit = () => {
    const { tournamentStartDate, tournamentEndDate } = this.props.post.details;
    const { idx, rateTypeId, payRate } = this.props.shiftLocation;
    const { zipCode } = this.props.shiftLocation.address;
    const { shifts } = this.props;
    const startDateIndex = moment(tournamentStartDate);
    const endDateIndex = moment(tournamentEndDate);
    if (shifts.length > 0 && shifts.some((shift) => shift.startDate)) return;

    const shouldAssignStartDate = this.props.shifts.every(
      (shift) => !!shift.id && !shift.startTime && !shift.endTime
    );

    if (shouldAssignStartDate) {
      shifts.forEach((shift) => {
        this.props.onChangeShift(shift.idx)({
          startDate: startDateIndex.format('YYYY-MM-DD'),
          endDate: startDateIndex.format('YYYY-MM-DD'),
        });
        startDateIndex.add(1, 'days');
      });
    }

    while (startDateIndex.isSameOrBefore(endDateIndex)) {
      const schedule = {
        startDate: startDateIndex.format('YYYY-MM-DD'),
        endDate: startDateIndex.format('YYYY-MM-DD'),
        start: '',
        end: '',
      };
      this.props.addShift({ locationId: idx, zipCode, schedule, rateTypeId, payRate });
      startDateIndex.add(1, 'days');
    }
  };

  componentDidMount() {
    if (!this.props.editMode) trackMixpanelPageView('create-event/agenda', this.props.user);

    const { hash } = this.props.location;
    const shiftIdx = hash.replace('#', '');
    if (shiftIdx) {
      setTimeout(() => {
        const el = document.getElementById(shiftIdx);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 500);
    }

    this.tournamentDaysInit();
  }

  backTo = () => {
    const { baseUrl } = this.props;
    return `${baseUrl}/location`;
  };

  onSaveAndContinue = () => {
    const { baseUrl, post } = this.props;

    if (!this.pageIsValid()) {
      this.props.validateForm();
      return;
    }

    const callback = () => {
      this.props.history.push(`${baseUrl}/schedule-summary`);
    };

    if (!this.props.authenticated) {
      callback();
      return;
    }

    this.props.saveAsDraft(post, callback);
  };

  pageIsValid = () => {
    const { isDraft } = this.props.post.details;
    if (!isDraft && !this.props.shifts.length) return true;

    const shifts = isDraft
      ? this.props.shifts
      : this.props.shifts.filter(({ endTime }) => isEndTimeAfterNow(endTime));

    return shifts.every((shift) => {
      return (
        !shift.errorMessages['startDate'] &&
        !shift.errorMessages['start'] &&
        !shift.errorMessages['end'] &&
        !shift.errorMessages['capacity']
      );
    });
  };

  getDays = (shifts) => {
    const days = shifts.map(({ startDate }) => moment(startDate).format('YYYY-MM-DD'));
    return Array.from(new Set(days)).sort((a, b) => moment(a).diff(b));
  };

  processShifts = (date) =>
    this.props.shifts.filter(({ startDate }) => moment(startDate).format('YYYY-MM-DD') === date);

  getLastDate = () => {
    const { shifts } = this.props;
    const lastDate = shifts.reduce((acc, { startDate }) => {
      return moment(startDate).isAfter(acc) ? moment(startDate) : acc;
    }, moment(this.props.post.details.tournamentStartDate));

    return lastDate;
  };

  addAnotherDay = () => {
    const { idx, rateTypeId, payRate } = this.props.shiftLocation;
    const { zipCode } = this.props.shiftLocation.address;
    const lastStartDate = this.getLastDate();
    const schedule = {
      startDate: moment(lastStartDate).add('days', 1).format('YYYY-MM-DD'),
      endDate: moment(lastStartDate).add('days', 1).format('YYYY-MM-DD'),
      start: '',
      end: '',
    };
    this.props.addShift({ locationId: idx, zipCode, schedule, rateTypeId, payRate });
  };

  renderDays = () => {
    const { tournamentStartDate, tournamentEndDate, isTournament } = this.props.post.details;
    const days = this.getDays(this.props.shifts);

    return (
      <React.Fragment>
        <ul className="days-list mb-3">
          {days.map((date, idx) => {
            const shifts = this.processShifts(date);

            return (
              <DaysListItem
                {...this.props}
                key={`day-${idx}`}
                date={date}
                shifts={shifts}
                open={true}
                idx={idx}
                tournamentEndDate={tournamentEndDate}
                tournamentStartDate={tournamentStartDate}
                isTournament={isTournament}
                days={days}
                editMode={this.props.editMode}
              />
            );
          })}
        </ul>
        <AddButton onClick={this.addAnotherDay}>ADD ANOTHER DAY</AddButton>
      </React.Fragment>
    );
  };

  render() {
    const { locationIdx } = this.props;
    return (
      <div className="tournamentSchedule">
        <div className="nav-placeholder" />
        <main>
          <header>
            <div className="screen-title">Location {locationIdx + 1} / Schedule</div>
          </header>
          <div className="content mb-5">{this.renderDays()}</div>
          <ActionBar
            editMode={this.props.editMode}
            backTo={this.backTo()}
            currentStep="schedule/tournament"
            onSaveAndContinue={this.onSaveAndContinue}
            nextDisabled={() => !this.pageIsValid()}
          />
        </main>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { post } = state;
  const { authenticated, currentUser } = state.session;
  const { wasValidated } = post.validations.shifts;
  const { locationIdx } = post.navigation;
  const shiftLocation = post.locations[locationIdx];
  const shifts = shiftsByLocation(post.shifts, shiftLocation);
  return {
    post,
    wasValidated,
    shiftLocation,
    locationIdx,
    authenticated,
    shifts,
    user: currentUser,
    eventName: post.profile.title,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  saveAsDraft: (post, callback) => dispatch(saveAsDraft({ post, callback, from: 'agenda' })),
  validateForm: () => dispatch(validateForm('shifts')),
  addShift: (payload) => dispatch(addShift(payload)),
  onDeleteShift: (idx: number) => () => dispatch(removeShift({ index: idx })),
  onChangeField: (index: number) => (name: string, value: any) =>
    dispatch(changeShiftField({ index, name, value })),
  onChangeShift: (index: number) => (shift) => dispatch(changeShift({ index, shift })),
  removeMultipleShifts: (indexes) => dispatch(removeMultipleShifts({ indexes })),
});

export default connect(mapStateToProps, mapDispatchToProps)(TournamentSchedule);
